<template>
  <div ref="wrapper" class="action-wrapper">
    <div v-if="show_dropdown" class="dropdown">
      <button class="btn btn-sm border btn-primary-dark dropdown-toggle rounded-pill" 
              type="button" 
              data-bs-toggle="dropdown" 
              aria-expanded="false">
        <i class="fas fa-ellipsis-v"></i>
      </button>
      <ul class="dropdown-menu dropdown-menu-end p-2">
        <slot :name="slotName" :row="row"></slot>
      </ul>
    </div>
    <div v-else class="d-flex gap-3">
      <slot :name="slotName" :row="row"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActionWrapper',
  props: {
    row: Object,
    slotName: {
      type: String,
      default: 'default'
    },
    maxButtons: {
      type: Number,
      default: 2
    },
    useIconList: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      show_dropdown: false,
      // Action to icon mapping
      action_icons: {
        edit: 'edit',
        update: 'edit',
        modify: 'edit',
        delete: 'trash',
        remove: 'trash',
        trash: 'trash',
        view: 'eye',
        see: 'eye',
        eye: 'eye',
        add: 'plus',
        create: 'plus',
        new: 'plus',
        download: 'download',
        upload: 'upload',
        copy: 'copy',
        clone: 'copy',
        print: 'print',
        save: 'save',
        send: 'paper-plane',
        mail: 'paper-plane',
        share: 'share',
        assign: 'tasks'
      }
    };
  },
  mounted() {
    this.check_button_count();
  },
  methods: {
    check_button_count() {
      this.$nextTick(() => {
        const wrapper = this.$refs.wrapper;
        // Only check buttons in the non-dropdown section
        const buttons = wrapper.querySelectorAll('.d-flex.gap-3 button');
        this.show_dropdown = buttons.length > this.maxButtons;
        
        // If using dropdown and icon list, style the dropdown items
        if (this.show_dropdown && this.use_icon_list) {
          this.style_dropdown_items();
        }
      });
    },
    
    style_dropdown_items() {
      this.$nextTick(() => {
        const dropdown_items = this.$refs.wrapper.querySelectorAll('.dropdown-menu button');
        dropdown_items.forEach(button => {
          // Remove ALL button classes to prevent Bootstrap button styling
          this.removeButtonClasses(button);
          // Add dropdown item classes
          button.classList.add('dropdown-item', 'd-flex', 'align-items-center', 'gap-2');
          // Store the original button for event handling
          this.store_original_button_data(button);
          // Add icon if button has text that indicates an action
          const button_text = button.textContent.toLowerCase().trim();
          this.add_icon_to_button(button, button_text);
        });
      });
    },
    
    remove_button_classes(button) {
      // Remove common button classes
      const classes_to_remove = [
        'btn', 'btn-sm', 'btn-lg', 'btn-xs',
        'btn-primary', 'btn-primary-dark', 'btn-secondary', 'btn-success', 
        'btn-danger', 'btn-warning', 'btn-info', 'btn-light', 'btn-dark',
        'btn-outline-primary', 'btn-outline-secondary', 'btn-outline-success',
        'btn-outline-danger', 'btn-outline-warning', 'btn-outline-info',
        'btn-outline-light', 'btn-outline-dark',
        'rounded', 'rounded-small', 'rounded-pill',
        'shadow', 'text-xxs', 'me-2', 'text-dark'
      ];
      
      classes_to_remove.forEach(className => {
        button.classList.remove(className);
      });
    },
    
    store_original_button_data(button) {
      // Store original classes and event handlers
      if (!button.dataset.originalClass) {
        button.dataset.originalClass = button.className;
      }
      if (!button.dataset.originalOnclick && button.onclick) {
        button.dataset.originalOnclick = button.onclick.toString();
      }
    },
    
    /**
     * Determine action type based on button text
     * @param {string} text - Button text in lowercase
     * @returns {string} Action type
     */
    determine_action_type(text) {
      const action_keywords = {
        edit: ['edit', 'update', 'modify'],
        delete: ['delete', 'remove', 'trash'],
        view: ['view', 'see', 'eye'],
        add: ['add', 'create', 'new'],
        download: ['download'],
        upload: ['upload'],
        copy: ['copy', 'clone'],
        print: ['print'],
        save: ['save'],
        send: ['send', 'mail'],
        share: ['share'],
        assign: ['assign']
      };

      for (const [action, keywords] of Object.entries(action_keywords)) {
        if (keywords.some(keyword => text.includes(keyword))) {
          return action;
        }
      }
      
      return 'default';
    },
    
    /**
     * Get icon class based on action type using Reflect
     * @param {string} actionType - Determined action type
     * @returns {string} Icon class
     */
    getIconClass(action_type) {
      const iconMap = {
        edit: 'edit',
        delete: 'trash',
        view: 'eye',
        add: 'plus',
        download: 'download',
        upload: 'upload',
        copy: 'copy',
        print: 'print',
        save: 'save',
        send: 'paper-plane',
        share: 'share',
        assign: 'tasks'
      };
      
      // Check if the actionType exists in the map
      if (Reflect.has(iconMap, action_type)) {
        return `fas fa-${Reflect.get(iconMap, action_type)}`;
      }
      
      return 'fas fa-cog'; // Default icon
    },
    
    /**
     * Add icon to button based on its text content
     * @param {HTMLElement} button - Button element
     * @param {string} text - Button text in lowercase
     */
    add_icon_to_button(button, text) {
      // Determine action type
      const action_type = this.determine_action_type(text);
      
      // Set action type attribute
      button.dataset.actionType = action_type;
      
      // Get icon class
      const icon_class = this.get_icon_class(action_type);
      
      // Create icon element
      const icon = document.createElement('i');
      icon.className = icon_class;
      icon.style.width = '16px';
      icon.style.textAlign = 'center';
      
      // Wrap text in span for better styling
      const text_span = document.createElement('span');
      text_span.textContent = button.textContent;
      text_span.className = 'dropdown-text';
      
      // Clear button content and add icon + text
      button.innerHTML = '';
      button.appendChild(icon);
      button.appendChild(text_span);
    }
  }
};
</script>

<style scoped>
/* Custom dropdown styling - Make these styles more specific */
.action-wrapper .dropdown-menu {
  min-width: 200px !important;
  border-radius: 10px !important;
  border: 1px solid #e0e0e0 !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  padding: 8px !important;
  margin-top: 8px !important;
}

/* Strong override for dropdown items */
.action-wrapper .dropdown-menu button.dropdown-item {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  padding: 10px 14px !important;
  margin: 4px 0 !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  color: #2c3e50 !important;
  background: transparent !important;
  border: none !important;
  width: 100% !important;
  text-align: left !important;
  cursor: pointer !important;
  box-shadow: none !important;
  text-transform: none !important;
  line-height: normal !important;
}

/* Remove any Bootstrap button styling */
.action-wrapper .dropdown-menu button.dropdown-item.btn,
.action-wrapper .dropdown-menu button.dropdown-item[class*="btn-"] {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Hover effects */
.action-wrapper .dropdown-menu button.dropdown-item:hover {
  background-color: #f8fafc !important;
  color: #1e293b !important;
  transform: translateX(4px) !important;
  padding-left: 18px !important;
}

/* Icon styling */
.action-wrapper .dropdown-menu button.dropdown-item i {
  font-size: 14px !important;
  width: 18px !important;
  text-align: center !important;
  color: #64748b !important;
  opacity: 0.9 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item:hover i {
  color: #3b82f6 !important;
  opacity: 1 !important;
}

/* Text styling */
.action-wrapper .dropdown-menu button.dropdown-item .dropdown-text {
  flex: 1 !important;
  white-space: nowrap !important;
}

/* Action type specific colors */
.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="edit"]:hover {
  background-color: rgba(59, 130, 246, 0.08) !important;
  color: #1d4ed8 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="edit"]:hover i {
  color: #1d4ed8 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="view"]:hover {
  background-color: rgba(16, 185, 129, 0.08) !important;
  color: #059669 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="view"]:hover i {
  color: #059669 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="share"]:hover {
  background-color: rgba(245, 158, 11, 0.08) !important;
  color: #d97706 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="share"]:hover i {
  color: #d97706 !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="assign"]:hover {
  background-color: rgba(139, 92, 246, 0.08) !important;
  color: #7c3aed !important;
}

.action-wrapper .dropdown-menu button.dropdown-item[data-action-type="assign"]:hover i {
  color: #7c3aed !important;
}

/* Dropdown toggle button styling */
.action-wrapper .dropdown .btn-primary-dark.dropdown-toggle {
  background-color: transparent !important;
  border-color: #cbd5e1 !important;
  color: #64748b !important;
  padding: 6px 14px !important;
  font-size: 12px !important;
}

.action-wrapper .dropdown .btn-primary-dark.dropdown-toggle:hover {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
  color: #475569 !important;
}

.action-wrapper .dropdown .btn-primary-dark.dropdown-toggle:focus {
  box-shadow: 0 0 0 3px rgba(203, 213, 225, 0.5) !important;
}

/* Regular button view styling */
.action-wrapper .d-flex.gap-3 {
  flex-wrap: wrap;
  gap: 0.75rem !important;
}

.action-wrapper .d-flex.gap-3 button {
  /* Regular buttons keep their original styling */
  flex-shrink: 0;
}
</style>

<style>
/* Global style to ensure dropdown items override Bootstrap */
.dropdown-menu .dropdown-item.btn,
.dropdown-menu button.btn {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  display: block !important;
  width: 100% !important;
  text-align: left !important;
}

/* Prevent any button styling in dropdowns */
.dropdown-menu button:not(.dropdown-toggle) {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0.5rem 1rem !important;
  width: 100% !important;
  text-align: left !important;
}
</style>